import React from "react";
import { Search, Loader2, Sparkles } from "lucide-react";
import { ALL_TLDS, GEN_TLDS, REGISTRARS, btnPrimary, btnGhost } from "./onboardShared";
import { ResultsTable } from "./ResultsTable";

function RegistrarToggle({ registrar, onSwitch, cfBuy, cfReg }) {
  return (
    <>
      <div className="label-tiny mb-2">Registrar</div>
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="domains-registrar-toggle">
        {REGISTRARS.map((r) => (
          <button key={r.key} data-testid={`domains-registrar-${r.key}`} onClick={() => onSwitch(r.key)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-sm border transition ${registrar === r.key ? "bg-amber-500 text-zinc-950 border-amber-500" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>
            <r.icon size={13} /> {r.label}
          </button>
        ))}
        <span className="text-[11px] text-zinc-500">{REGISTRARS.find((r) => r.key === registrar)?.hint}</span>
      </div>
      {cfBuy && cfReg && !cfReg.ok && (
        <div data-testid="domains-cfreg-warning" className="mb-4 border border-rose-500/30 bg-rose-500/5 text-rose-300 px-3 py-2 rounded-sm text-xs leading-relaxed">
          <b>Cloudflare Registrar isn't reachable:</b> {cfReg.error}. Tokens are created at dash.cloudflare.com → My Profile → API Tokens; the account also needs a default payment method and a default registrant contact (Domain Registration page) before buying.
        </div>
      )}
      {cfBuy && cfReg?.ok && (
        <div className="mb-4 text-[11px] text-sky-300/90 leading-relaxed">
          Cloudflare sells at cost (no markup). Purchases charge your Cloudflare account's default card and are non-refundable. Premium names and a few TLDs aren't purchasable via API — they'll show as unavailable with a reason.
        </div>
      )}
    </>
  );
}

function TldChips({ all, active, onToggle, testPrefix, activeCls }) {
  return (
    <div className="flex flex-wrap gap-2">
      {all.map((t) => (
        <button key={t} data-testid={`${testPrefix}-${t}`} onClick={() => onToggle(t)} className={`mono text-xs px-2.5 py-1 rounded-sm border transition ${active.has(t) ? activeCls : "border-zinc-700 text-zinc-400 hover:border-zinc-500"}`}>.{t}</button>
      ))}
    </div>
  );
}

function SearchCard({ buy, cfBuy, cfReg }) {
  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
      <RegistrarToggle registrar={buy.registrar} onSwitch={buy.switchRegistrar} cfBuy={cfBuy} cfReg={cfReg} />
      <div className="label-tiny mb-2">Keywords (one per line or comma-separated)</div>
      <textarea data-testid="domains-keywords" value={buy.keywords} onChange={(e) => buy.setKeywords(e.target.value)} rows={3} placeholder={"frontierfinance\nmarketpulse\ntradewise"} className="admin-input w-full bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" />
      <div className="label-tiny mt-4 mb-2">Extensions</div>
      <TldChips all={ALL_TLDS} active={buy.tlds} onToggle={buy.toggleTld} testPrefix="domains-tld" activeCls="bg-white text-zinc-950 border-white" />
      <div className="flex flex-wrap items-center gap-2 mt-5">
        <button data-testid="domains-search-btn" onClick={buy.doSearch} disabled={buy.searching || !buy.keywords.trim() || buy.tlds.size === 0} className={btnPrimary}>
          {buy.searching ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />} Check availability
        </button>
        {cfBuy && (
          <button data-testid="domains-suggest-btn" onClick={buy.doSuggest} disabled={buy.suggesting || !buy.keywords.trim()} title="Cloudflare's own name ideas for your keywords (filtered to the selected extensions)" className={btnGhost}>
            {buy.suggesting ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Cloudflare suggestions
          </button>
        )}
      </div>
    </div>
  );
}

function BrandableGenerator({ buy }) {
  return (
    <div className="border border-amber-500/20 bg-amber-500/[0.03] rounded-sm p-5">
      <div className="flex items-center gap-2 mb-1"><Sparkles size={15} className="text-amber-400" /><div className="font-display text-base font-bold">Brandable name generator</div></div>
      <div className="text-xs text-zinc-500 mb-3">Auto-propose available finance-flavored names across premium TLDs.</div>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <div className="label-tiny mb-1.5">How many</div>
          <input data-testid="domains-gen-count" type="number" min="4" max="30" value={buy.genCount} onChange={(e) => buy.setGenCount(e.target.value)} className="admin-input w-20 bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono" />
        </div>
        <div>
          <div className="label-tiny mb-1.5">Premium TLDs</div>
          <TldChips all={GEN_TLDS} active={buy.genTlds} onToggle={buy.toggleGenTld} testPrefix="domains-gen-tld" activeCls="bg-amber-500 text-zinc-950 border-amber-500" />
        </div>
        <button data-testid="domains-generate-btn" onClick={() => buy.doGenerate()} disabled={buy.generating || buy.genTlds.size === 0} className={btnPrimary}>
          {buy.generating ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
        </button>
      </div>
    </div>
  );
}

/** Collect phase — Buy lane: registrar, keyword search, brandable generator, results. */
export function BuyLane({ w }) {
  return (
    <div className="mt-5 space-y-5">
      <SearchCard buy={w.buy} cfBuy={w.cfBuy} cfReg={w.cfReg} />
      <BrandableGenerator buy={w.buy} />
      {w.buy.results && <ResultsTable w={w} />}
    </div>
  );
}
